/**
 * useClinicalHudAccess — gate for ClinicalHUD product surfaces and SpecialtyKB DDx.
 *
 * True when:
 * - JWT global admin, OR
 * - clinicalHudAccess/{uid} exists
 *
 * Intentionally does NOT include DP invite or dev-auth bypass.
 */
import { useEffect, useState } from 'react';
import { useAuth } from 'hooks/useAuth';
import { subscribeToClinicalHudAccess } from '../services/clinicalHudAccess';

export function useClinicalHudAccess() {
  const { user, isGlobalAdmin, userLoading } = useAuth();
  const [hasDocAccess, setHasDocAccess] = useState(false);
  const [docLoading, setDocLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;
  const privileged = Boolean(isGlobalAdmin);

  useEffect(() => {
    if (userLoading) {
      setDocLoading(true);
      return undefined;
    }

    if (!isRealAccount || privileged) {
      setHasDocAccess(false);
      setAccessError(false);
      setDocLoading(false);
      return undefined;
    }

    setDocLoading(true);
    const unsub = subscribeToClinicalHudAccess(
      uid,
      (data) => {
        setAccessError(false);
        setHasDocAccess(Boolean(data));
        setDocLoading(false);
      },
      () => {
        setHasDocAccess(false);
        setAccessError(true);
        setDocLoading(false);
      },
    );
    return () => unsub();
  }, [uid, isRealAccount, userLoading, privileged]);

  const canAccess = Boolean(isRealAccount && (privileged || hasDocAccess));
  const loading = Boolean(
    userLoading || (isRealAccount && !privileged && docLoading),
  );

  return { canAccess: loading ? false : canAccess, loading, error: accessError };
}

export default useClinicalHudAccess;
